import React from 'react'
import { Button } from '../ui/Button'
import {
  fetchVulnerabilityContext,
  fetchControlContext,
  fetchRiskContext,
} from '../../services/informationSystemOperationalApi'
import { linkRiskToSystem, unlinkRiskFromSystem } from '../../services/informationSystemPhase15Api'
import type { InformationSystemPanelContext } from './informationSystemPanelContext'

export type DrillDownTarget =
  | { kind: 'vulnerability'; id?: string; label?: string; systemId?: string }
  | { kind: 'control'; id?: string; label?: string; systemId?: string }
  | { kind: 'risk'; sourceType: string; sourceId: string; label?: string; systemId?: string }
  | null

export function panelContextToDrillTarget(ctx: InformationSystemPanelContext): DrillDownTarget {
  if (ctx.kind === 'vulnerability' && ctx.vulnerabilityId) {
    return { kind: 'vulnerability', id: ctx.vulnerabilityId, label: ctx.label, systemId: ctx.systemId }
  }
  if (ctx.kind === 'control' && ctx.controlLinkId) {
    return { kind: 'control', id: ctx.controlLinkId, label: ctx.label, systemId: ctx.systemId }
  }
  if (ctx.kind === 'risk' && ctx.sourceType && ctx.sourceId) {
    return {
      kind: 'risk',
      sourceType: ctx.sourceType,
      sourceId: ctx.sourceId,
      label: ctx.label,
      systemId: ctx.systemId,
    }
  }
  return null
}

export function useDrillDownData(
  target: DrillDownTarget,
  token: string | null,
  orgId: string | null,
  t: (key: string, fallback?: string) => string
) {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [data, setData] = React.useState<Record<string, unknown> | null>(null)

  React.useEffect(() => {
    if (!target || !token) {
      setData(null)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        let result: Record<string, unknown>
        if (target.kind === 'vulnerability') {
          if (!target.id) {
            if (!cancelled) setData({})
            return
          }
          result = await fetchVulnerabilityContext(token, target.id, orgId)
        } else if (target.kind === 'control') {
          if (!target.id) {
            if (!cancelled) setData({})
            return
          }
          result = await fetchControlContext(token, target.id, orgId)
        } else {
          result = await fetchRiskContext(token, target.sourceType, target.sourceId, orgId)
        }
        if (!cancelled) setData(result)
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t('assets.operational.drillDown.error', 'Failed to load details'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [target, token, orgId, t])

  return { loading, error, data }
}

export function DrillDownContent({
  kind,
  data,
  t,
  target,
  systemId,
  token,
  orgId,
  onOpenGovernance,
}: {
  kind: 'vulnerability' | 'control' | 'risk'
  data: Record<string, unknown>
  t: (key: string, fallback?: string) => string
  target: NonNullable<DrillDownTarget>
  systemId?: string
  token: string | null
  orgId: string | null
  onOpenGovernance?: () => void
}) {
  if (kind === 'vulnerability') {
    const v = data.vulnerability as Record<string, unknown> | undefined
    return (
      <div className="contextSectionBody">
        {v?.cve ? (
          <p>
            <span className="contextMetaLabel">CVE:</span> {String(v.cve)}
          </p>
        ) : null}
        {v?.severity ? (
          <p>
            <span className="contextMetaLabel">{t('assets.operational.severity', 'Severity')}:</span> {String(v.severity)}
          </p>
        ) : null}
        {v?.cvssScore != null ? (
          <p>
            <span className="contextMetaLabel">CVSS:</span> {String(v.cvssScore)}
          </p>
        ) : null}
        {v?.description ? <p className="contextDescription">{String(v.description)}</p> : null}
        <JsonSection label={t('assets.operational.relatedControls', 'Related controls')} value={data.relatedControls} />
        <JsonSection label={t('assets.operational.affectedAssets', 'Affected assets')} value={data.affectedAssets} />
        {systemId && token ? (
          <div className="contextActions">
            <Button type="button" size="small" color="outline" onClick={() => window.open('/compliance/remediation', '_blank')}>
              {t('assets.workflows.createRemediation', 'Create remediation')}
            </Button>
            {onOpenGovernance ? (
              <Button type="button" size="small" color="outline" onClick={onOpenGovernance}>
                {t('assets.intelligence.openGovernance', 'Open Governance')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }
  if (kind === 'control') {
    const c = data.control as Record<string, unknown> | undefined
    const fa = data.failureAnalysis as { reasons?: string[] } | undefined
    return (
      <div className="contextSectionBody">
        {c?.controlTitle ? <p className="contextTitle">{String(c.controlTitle)}</p> : null}
        {fa?.reasons?.length ? (
          <ul className="contextReasonList">
            {fa.reasons.map((r) => (
              <li key={r}>{r.replace(/_/g, ' ').toLowerCase()}</li>
            ))}
          </ul>
        ) : null}
        <JsonSection label={t('assets.grc.evidence', 'Evidence')} value={data.evidence} />
        <JsonSection label={t('assets.grc.remediation', 'Remediation')} value={data.remediation} />
      </div>
    )
  }
  const r = data.risk as Record<string, unknown> | undefined
  const riskId = target.kind === 'risk' && target.sourceType === 'RISK_REGISTER' ? target.sourceId : null
  return (
    <div className="contextSectionBody">
      {r?.title ? <p className="contextTitle">{String(r.title)}</p> : null}
      <JsonSection label={t('assets.operational.linkedControls', 'Linked controls')} value={data.linkedControls} />
      <div className="contextActions">
        {onOpenGovernance ? (
          <Button type="button" size="small" color="outline" onClick={onOpenGovernance}>
            {t('assets.intelligence.openGovernance', 'Open Governance')}
          </Button>
        ) : null}
        {riskId && systemId && token ? (
          <>
            <Button type="button" size="small" onClick={() => linkRiskToSystem(token, systemId, riskId, orgId)}>
              {t('assets.intelligence.linkRisk', 'Link to system')}
            </Button>
            <Button type="button" size="small" color="outline" onClick={() => unlinkRiskFromSystem(token, systemId, riskId, orgId)}>
              {t('assets.intelligence.unlinkRisk', 'Unlink')}
            </Button>
          </>
        ) : null}
        <Button type="button" size="small" color="outline" onClick={() => window.open('/governance/evidence', '_blank')}>
          {t('assets.intelligence.requestEvidence', 'Request evidence')}
        </Button>
        <Button type="button" size="small" color="outline" onClick={() => window.open('/compliance/remediation', '_blank')}>
          {t('assets.workflows.openRemediation', 'Open remediation')}
        </Button>
      </div>
    </div>
  )
}

function JsonSection({ label, value }: { label: string; value: unknown }) {
  if (value == null || (Array.isArray(value) && value.length === 0)) return null
  return (
    <div className="contextJsonBlock">
      <p className="contextJsonLabel">{label}</p>
      <pre className="contextJsonPre">{JSON.stringify(value, null, 2)}</pre>
    </div>
  )
}
